import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import dayjs from 'dayjs'
import * as jp from 'json-p3/dist/json-p3.esm'

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(T|\s)\d{2}:\d{2}/

const useStyles = makeStyles(() => ({
  readonlyField: {
    padding: 'var(--padding-md)',
    backgroundColor: 'var(--color-surface-100)',
    borderRadius: 'var(--border-radius-sm)',
    color: 'var(--color-neutral-800)',
    fontSize: 'var(--text-size-md)',
    marginBottom: 'var(--margin-md)'
  },
  label: {
    fontWeight: 'var(--text-weight-bold)',
    marginRight: 'var(--margin-sm)'
  }
}))

function formatDisplayValue (raw, format) {
  if (raw == null) return null
  if (format) {
    const parsed = dayjs(raw)
    if (parsed.isValid()) return parsed.format(format)
  }
  if (typeof raw === 'string' && ISO_DATE_PATTERN.test(raw)) {
    const parsed = dayjs(raw)
    if (parsed.isValid()) return parsed.format('MMM D, YYYY h:mm A')
  }
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No'
  if (typeof raw === 'number') return raw.toLocaleString()
  return String(raw)
}

function ReadonlyAccessorEditor ({ field, initializationData }) {
  const classes = useStyles()

  const rawValue = useMemo(() => {
    if (!initializationData) return null

    if (field.accessor) {
      try {
        const results = jp.query(field.accessor, initializationData)
        return results?.nodes[0]?.value ?? null
      } catch (error) {
        console.error('Error evaluating JSON path:', error)
        return null
      }
    }

    return initializationData[field.id] ?? null
  }, [initializationData, field.accessor, field.id])

  const displayValue = useMemo(
    () => formatDisplayValue(rawValue, field.format),
    [rawValue, field.format]
  )

  return (
    <div className={classes.readonlyField}>
      {field.label && <span className={classes.label}>{field.label}:</span>}
      <span>{displayValue ?? 'N/A'}</span>
    </div>
  )
}

ReadonlyAccessorEditor.propTypes = {
  field: PropTypes.shape({
    label: PropTypes.string,
    accessor: PropTypes.string,
    id: PropTypes.string,
    format: PropTypes.string
  }).isRequired,
  initializationData: PropTypes.any
}

export default ReadonlyAccessorEditor
